import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { ReactComponent as PlayersLoungeLogo } from "../../assets/logo/players-lounge-logo.svg"
import {
  buildOverlayUrl,
  DEFAULT_SETTINGS,
  getOverlaySettings,
  hasSettingsInUrl,
  OverlaySettings,
  Placement,
  PLACEMENTS,
  Theme,
  THEMES,
  WinAnimation,
  WIN_ANIMATIONS,
  WinSound,
  WIN_SOUNDS,
} from "../../helpers/overlay-settings"
import { trackEvent } from "../../helpers/logger"
import { getPreviewUserIdFromUrl, normaliseUserId } from "../../helpers/user-id"
import { useUserLookup } from "./use-user-lookup"
import { StreamVideo } from "./stream-video"
import { WinCelebration } from "../win-celebration/win-celebration"
import { InternalizationProvider } from "../../context/cms-context"
import { BetSlipFragment } from "../../graphql/core-types"
import { withSampleStakers } from "../../helpers/sample-stakers"
import './url-builder.css'

// the overlay is laid out for a 1080p browser source, the preview scales that canvas down to fit
const CANVAS_WIDTH = 1920
const CANVAS_HEIGHT = 1080
const COPIED_RESET_MS = 2000
const STORAGE_KEY = 'overlay-url-builder'

const PLACEMENT_LABELS: Record<Placement, string> = { left: 'Left', right: 'Right', off: 'Off' }
const THEME_LABELS: Record<Theme, string> = { auto: 'Auto', light: 'Light', dark: 'Dark' }
const WIN_ANIMATION_LABELS: Record<WinAnimation, string> = { fullscreen: 'Full screen', muted: 'Muted', off: 'Off' }
const WIN_SOUND_LABELS: Record<WinSound, string> = { on: 'On', muted: 'Muted' }

// a made up win for the preview button, only the fields the celebrations read. part paid with bet credits so the
// preview shows how credits are marked
const SAMPLE_WON_BET_SLIP = {
  id: 'url-builder-sample-win',
  payout: '125.00',
  betTotalAmount: '25.00',
  payoutAmounts: [{ wallet: 'PL_BALANCE', amount: '125.00' }],
  betItems: [{
    gameSlug: 'fortnite',
    betAmount: '25.00',
    payoutMultiplier: '5',
    fundingSources: [{ wallet: 'PL_BALANCE', amount: '20.00' }, { wallet: 'BET_CREDITS', amount: '5.00' }],
  }],
} as unknown as BetSlipFragment

// with the stakers preview on, the sample win is backed by a few of the sample stakers too
const SAMPLE_WIN_STAKER_COUNT = 4

type SavedState = { userIdInput: string, settings: OverlaySettings }

// remembered per browser so a streamer coming back to tweak their layout doesn't start over
const loadSavedState = (): SavedState | null => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    if (!parsed || typeof parsed.userIdInput !== 'string') return null
    return {
      userIdInput: parsed.userIdInput,
      settings: { ...DEFAULT_SETTINGS, ...parsed.settings, positions: { ...DEFAULT_SETTINGS.positions, ...parsed.settings?.positions } },
    }
  } catch {
    return null
  }
}

const saveState = (state: SavedState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // private browsing, it just won't be remembered
  }
}

// clipboard api needs a secure context, fall back to the old select and copy trick
const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(textarea)
    return copied
  }
}

type SegmentedControlProps<T extends string> = {
  label: string
  options: T[]
  labels: Record<T, string>
  value: T
  onChange: (value: T) => void
}

const SegmentedControl = <T extends string>({ label, options, labels, value, onChange }: SegmentedControlProps<T>) => (
  <div className="url_builder__field">
    <span className="url_builder__label pl-fontSContrast">{label}</span>
    <div className="url_builder__segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={`url_builder__segment pl-fontSContrast ${option === value ? 'url_builder__segment--selected' : ''}`}
          aria-pressed={option === value}
          onClick={() => onChange(option)}>
          {labels[option]}
        </button>
      ))}
    </div>
  </div>
)

type OverlayPreviewProps = {
  src: string | null
  // the link the streamer copies, opened full size. never the preview url, so no preview only flags leak out
  href: string | null
  emptyMessage: string
  // the streamer's live feed plays under the overlay, the plain stand in shows without one
  streamUrl: string | null
  onStreamFailed: (streamUrl: string) => void
}

const OverlayPreview = ({ src, href, emptyMessage, streamUrl, onStreamFailed }: OverlayPreviewProps) => {
  const frameRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0)

  useLayoutEffect(() => {
    const frame = frameRef.current
    if (!frame) return
    const updateScale = () => setScale(frame.clientWidth / CANVAS_WIDTH)
    updateScale()
    const resizeObserver = new ResizeObserver(updateScale)
    resizeObserver.observe(frame)
    return () => resizeObserver.disconnect()
  }, [])

  return (
    <div ref={frameRef} className="url_builder__preview">
      {streamUrl ? (
        <StreamVideo
          key={streamUrl}
          src={streamUrl}
          className="url_builder__preview_stream"
          onError={() => onStreamFailed(streamUrl)}
        />
      ) : src && (
        // only behind a running overlay, the empty state has its own message in the same spot
        <span className="url_builder__preview_backdrop pl-fontL">Your stream</span>
      )}
      {src ? (
        <>
          <iframe
            // remount on every change so the overlay replays its entrance like it will in obs
            key={src}
            src={src}
            title="Overlay preview"
            className="url_builder__preview_frame"
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            style={{ transform: `scale(${scale})` }}
          />
          {/* the iframe ignores the pointer so the overlay can't be clicked into, this opens the real link full size instead */}
          {href && <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="url_builder__preview_link"
            aria-label="Open your overlay link in a new tab"
            onClick={() => trackEvent('url_builder_preview_opened')}
          />}
        </>
      ) : (
        <span className="url_builder__preview_empty pl-fontS">{emptyMessage}</span>
      )}
    </div>
  )
}

// a setup link (?previewUserId=...&bets=left...) wins over what this browser remembered, then defaults
const getInitialState = (savedState: SavedState | null): SavedState => ({
  userIdInput: getPreviewUserIdFromUrl() ?? savedState?.userIdInput ?? '',
  settings: hasSettingsInUrl() ? getOverlaySettings() : savedState?.settings ?? DEFAULT_SETTINGS,
})

export const UrlBuilder = () => {
  const [savedState] = useState(loadSavedState)
  const [initialState] = useState(() => getInitialState(savedState))
  const [userIdInput, setUserIdInput] = useState(initialState.userIdInput)
  const [settings, setSettings] = useState<OverlaySettings>(initialState.settings)
  const [hasCopied, setHasCopied] = useState(false)
  // bumped per click so a second click restarts the celebration instead of being ignored
  const [winPreviewRun, setWinPreviewRun] = useState<number | null>(null)
  // preview only, never saved or put in the overlay link
  const [previewStakers, setPreviewStakers] = useState(false)

  const userId = normaliseUserId(userIdInput)
  const userLookup = useUserLookup(userId)
  const isUserNotFound = userLookup.status === 'not-found'
  const showUserIdError = (userIdInput.trim() !== '' && !userId) || isUserNotFound
  // an id with no account would give a link that shows nothing on stream, so don't hand one out
  const linkUserId = isUserNotFound ? null : userId
  const baseUrl = `${window.location.origin}${window.location.pathname}`
  const overlayUrl = linkUserId ? buildOverlayUrl(baseUrl, linkUserId, settings) : null
  const previewUrl = linkUserId ? buildOverlayUrl(baseUrl, linkUserId, settings, { preview: true, previewStakers }) : null
  const twitch = userLookup.status === 'found' ? userLookup.twitch : null
  // the url that failed to play, so a new one (another user, or the stream restarting) gets a fresh try
  const [failedStreamUrl, setFailedStreamUrl] = useState<string | null>(null)
  const playableStreamUrl = twitch?.streamUrl && twitch.streamUrl !== failedStreamUrl ? twitch.streamUrl : null
  // says why the preview is or isn't showing their stream, otherwise a missing feed just looks broken
  const previewStreamNote = userLookup.status !== 'found' ? null
    : !twitch ? 'Link Twitch in the Players\' Lounge app to preview the overlay over your stream.'
    : playableStreamUrl ? null
    : twitch.streamUrl ? `Couldn't load the live stream from twitch.tv/${twitch.channel}, so the preview uses a stand-in. The stream link may have expired, it refreshes next time you go live.`
    : `twitch.tv/${twitch.channel} isn't live right now, so the preview uses a stand-in. Go live to see the overlay over your stream.`
  const previewEmptyMessage = isUserNotFound
    ? "No account found for that user ID, so there's nothing to preview"
    : 'Enter your user ID to see a live preview'

  useEffect(() => {
    // prefilled means the streamer arrived on a setup link we sent them
    trackEvent('url_builder_opened', { returning: Boolean(savedState), prefilled: Boolean(getPreviewUserIdFromUrl()) })
  }, [savedState])

  useEffect(() => {
    saveState({ userIdInput, settings })
  }, [userIdInput, settings])

  useEffect(() => {
    if (!hasCopied) return
    const timeout = setTimeout(() => setHasCopied(false), COPIED_RESET_MS)
    return () => clearTimeout(timeout)
  }, [hasCopied])

  const setPosition = (element: keyof OverlaySettings['positions']) => (placement: Placement) =>
    setSettings((current) => ({ ...current, positions: { ...current.positions, [element]: placement } }))

  const handleCopy = async () => {
    if (!overlayUrl) return
    const copied = await copyText(overlayUrl)
    setHasCopied(copied)
    if (copied) {
      trackEvent('url_builder_copied', {
        theme: settings.theme,
        badgePosition: settings.positions.liveBadge,
        betsPosition: settings.positions.betList,
        qrPosition: settings.positions.referralQr,
        winAnimation: settings.winAnimation,
        winSound: settings.winSound,
      })
    }
  }

  return (
    <main className="url_builder">
      <div className="url_builder__content">
        <header className="url_builder__header">
          <PlayersLoungeLogo className="url_builder__logo" />
          <h1 className="url_builder__title pl-fontXL">Stream overlay</h1>
          <p className="url_builder__intro pl-fontM">
            Show your live bets, wins and referral code on stream. Set it up below, then add the link to OBS as a browser source.
          </p>
        </header>

        <div className="url_builder__layout">
          <section className="url_builder__panel">
            <label className="url_builder__field">
              <span className="url_builder__label pl-fontSContrast">Players' Lounge user ID</span>
              <input
                className={`url_builder__input pl-fontM ${showUserIdError ? 'url_builder__input--error' : ''}`}
                value={userIdInput}
                onChange={(event) => setUserIdInput(event.target.value)}
                placeholder="e.g. 7d0c2c3e-3f4a-4b8e-9a51-2f6d1e0b9c11"
                spellCheck={false}
                autoComplete="off"
                aria-invalid={showUserIdError}
              />
              {showUserIdError && !isUserNotFound && (
                <span className="url_builder__error pl-fontXS">That doesn't look like a user ID. It should be a UUID like the example.</span>
              )}
              {isUserNotFound && (
                <span className="url_builder__error pl-fontXS">We couldn't find a Players' Lounge account with that user ID. Check you copied the whole thing.</span>
              )}
              {userLookup.status === 'checking' && (
                <span className="url_builder__hint pl-fontXS">Checking your user ID…</span>
              )}
              {userLookup.status === 'found' && (
                <span className="url_builder__found pl-fontXS">
                  {userLookup.username ? `Found ${userLookup.username}` : 'Found your account'}
                </span>
              )}
              {userLookup.status === 'found' && (userLookup.twitch ? (
                <a
                  className="url_builder__twitch"
                  href={userLookup.twitch.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent('url_builder_twitch_opened', { live: userLookup.twitch?.isLive })}>
                  {userLookup.twitch.imageUrl && <img className="url_builder__twitch_avatar" src={userLookup.twitch.imageUrl} alt="" />}
                  <span className="url_builder__twitch_label pl-fontSContrast">{userLookup.twitch.label}</span>
                  {userLookup.twitch.isLive && <span className="url_builder__twitch_live pl-fontXSContrast">Live</span>}
                </a>
              ) : (
                <span className="url_builder__hint pl-fontXS">No Twitch account is linked to this user yet. The overlay still works, link Twitch in the Players' Lounge app to stream with it.</span>
              ))}
              {userLookup.status === 'unavailable' && (
                <span className="url_builder__hint pl-fontXS">We couldn't check this user ID right now. The link will still work if the ID is right.</span>
              )}
            </label>

            <div className="url_builder__win_row">
              <SegmentedControl label="Bet card" options={PLACEMENTS} labels={PLACEMENT_LABELS} value={settings.positions.betList} onChange={setPosition('betList')} />
              <button
                type="button"
                className="url_builder__copy url_builder__win_preview url_builder__stakers_preview pl-fontSContrast"
                disabled={settings.positions.betList === 'off'}
                aria-pressed={previewStakers}
                onClick={() => {
                  setPreviewStakers((current) => !current)
                  trackEvent('url_builder_stakers_previewed', { on: !previewStakers })
                }}>
                {previewStakers ? 'Hide stakers' : 'Preview stakers'}
              </button>
            </div>
            <SegmentedControl label="QR code" options={PLACEMENTS} labels={PLACEMENT_LABELS} value={settings.positions.referralQr} onChange={setPosition('referralQr')} />
            <SegmentedControl label="Live badge" options={PLACEMENTS} labels={PLACEMENT_LABELS} value={settings.positions.liveBadge} onChange={setPosition('liveBadge')} />
            <div className="url_builder__win_row">
              <SegmentedControl
                label="Win celebration"
                options={WIN_ANIMATIONS}
                labels={WIN_ANIMATION_LABELS}
                value={settings.winAnimation}
                onChange={(winAnimation) => setSettings((current) => ({ ...current, winAnimation }))}
              />
              <button
                type="button"
                className="url_builder__copy url_builder__win_preview pl-fontSContrast"
                disabled={settings.winAnimation === 'off'}
                onClick={() => {
                  setWinPreviewRun((run) => (run ?? 0) + 1)
                  trackEvent('url_builder_win_previewed', { style: settings.winAnimation, sound: settings.winSound })
                }}>
                Preview
              </button>
            </div>
            <SegmentedControl
              label="Win sound"
              options={WIN_SOUNDS}
              labels={WIN_SOUND_LABELS}
              value={settings.winSound}
              onChange={(winSound) => setSettings((current) => ({ ...current, winSound }))}
            />
            <SegmentedControl
              label="Theme"
              options={THEMES}
              labels={THEME_LABELS}
              value={settings.theme}
              onChange={(theme) => setSettings((current) => ({ ...current, theme }))}
            />

            <div className="url_builder__field">
              <span className="url_builder__label pl-fontSContrast">Your overlay link</span>
              <div className="url_builder__link_row">
                <input
                  className="url_builder__input url_builder__input--link pl-fontS"
                  value={overlayUrl ?? ''}
                  placeholder={isUserNotFound ? 'Enter a valid user ID first' : 'Enter your user ID first'}
                  readOnly
                  onFocus={(event) => event.target.select()}
                />
                <button
                  type="button"
                  className="url_builder__copy pl-fontSContrast"
                  onClick={handleCopy}
                  disabled={!overlayUrl}>
                  {hasCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </section>

          <section className="url_builder__panel url_builder__panel--preview">
            <span className="url_builder__label pl-fontSContrast">Preview</span>
            <OverlayPreview
              src={previewUrl}
              href={overlayUrl}
              emptyMessage={previewEmptyMessage}
              streamUrl={playableStreamUrl}
              onStreamFailed={setFailedStreamUrl}
            />
            {previewStreamNote && <p className="url_builder__hint pl-fontXS">{previewStreamNote}</p>}
            <p className="url_builder__hint pl-fontXS">
              The bet card shows your newest pending bet (or "No active bets" when there isn't one), and the win celebration plays when a bet pays out more than you wagered.
              When viewers stake on your bet, their avatars show on the card and it swaps between your numbers and the totals with stakers. Preview stakers fills in made up stakers so you can see it.
            </p>
            <ol className="url_builder__steps pl-fontS">
              <li>In OBS, add a <strong>Browser</strong> source to your scene.</li>
              <li>Paste your overlay link into the URL field.</li>
              <li>Set the width to <strong>1920</strong> and the height to <strong>1080</strong>.</li>
            </ol>
          </section>
        </div>
      </div>

      {/* played over the whole builder rather than inside the scaled down iframe, so it reads at real size */}
      {winPreviewRun !== null && settings.winAnimation !== 'off' && (
        <InternalizationProvider>
          <WinCelebration
            key={winPreviewRun}
            style={settings.winAnimation}
            sound={settings.winSound}
            betSlip={previewStakers ? withSampleStakers(SAMPLE_WON_BET_SLIP, SAMPLE_WIN_STAKER_COUNT) : SAMPLE_WON_BET_SLIP}
            betList={settings.positions.betList}
            onComplete={() => setWinPreviewRun(null)}
          />
        </InternalizationProvider>
      )}
    </main>
  )
}
